import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Spin } from "antd";
import dayjs from "dayjs";
import { previewBillingPolicyApi } from "../../../../../services/billingPolicies.services";
import { IconArrowRight, IconClock, IconFlask, IconPlus, IconTrash } from "./icons";
import {
  FRACTION_CLASS,
  FRACTION_SHORT,
  MODE_LABELS,
  RULE_LABELS,
  apiError,
  configurationErrors,
  fh,
  formatAmount,
} from "./policyUtils";
import styles from "./BillingPolicies.module.css";

const FIELD = "YYYY-MM-DDTHH:mm";

let stayKey = 0;
const stay = (kind, start, end, rate) => ({ key: ++stayKey, kind, start, end, rate });

/* Sample stays anchored two days back, so they read like recent admissions
   rather than dates from the design board. */
const buildScenario = (name) => {
  const d = dayjs().startOf("day").subtract(2, "day");
  switch (name) {
    case "nights":
      return [stay("bed", d.hour(22), d.add(2, "day").hour(8), 1000)];
    case "long":
      return [stay("bed", d.hour(9), d.add(2, "day").hour(11).minute(30), 1000)];
    case "sameDay":
      return [stay("bed", d.hour(10), d.hour(15), 1000)];
    case "transfer":
      return [
        stay("bed", d.hour(14), d.add(1, "day").hour(18), 1000),
        stay("bed", d.add(1, "day").hour(18), d.add(3, "day").hour(10), 4500),
      ];
    case "package":
      return [
        stay("bed", d.hour(20), d.add(3, "day").hour(9), 1000),
        stay("package", d.add(1, "day").hour(8), d.add(3, "day").hour(9), 6000),
      ];
    default:
      return [];
  }
};

const SCENARIOS = [
  { key: "nights", label: "22:00 → 08:00, 2 nights" },
  { key: "long", label: "50.5 hours" },
  { key: "sameDay", label: "Same day, 5h" },
  { key: "transfer", label: "Bed transfer" },
  { key: "package", label: "Bed + package" },
];

// No bed names on the form - the breakdown calls them Bed 1, Bed 2, Package 1
// in the order they were added.
const stayLabels = (stays) => {
  const seen = { bed: 0, package: 0 };
  return stays.map((s) => {
    seen[s.kind] += 1;
    return `${s.kind === "package" ? "Package" : "Bed"} ${seen[s.kind]}`;
  });
};

const toPayloadStays = (stays, labels) =>
  stays.map((s, index) => ({
    kind: s.kind,
    label: labels[index],
    start: s.start?.format(),
    end: s.end?.format(),
    rate: Number(s.rate) || 0,
  }));

// Backend times carry the facility's offset; read them as facility wall time.
const wall = (iso) => (iso ? dayjs(String(iso).slice(0, 19)) : null);

const PeriodRows = ({ periods }) => {
  const grouped = useMemo(() => new Set(periods.map((p) => p.stay)).size > 1, [periods]);
  let lastGroup = null;

  return periods.map((period, index) => {
    const start = wall(period.start);
    const end = wall(period.end);
    const header = grouped && period.stay !== lastGroup ? period.stay : null;
    lastGroup = period.stay;
    const fraction = Number(period.fraction);
    const sameDate = start && end && start.isSame(end, "day");

    return (
      <React.Fragment key={`${period.stay}-${period.start}-${index}`}>
        {header && (
          <tr className={styles.grp}>
            <td colSpan={6}>{header}</td>
          </tr>
        )}
        <tr>
          <td className={styles.dte}>
            <b>{start ? start.format("DD MMM") : "-"}</b>
            <small>
              {start ? start.format("HH:mm") : "-"} →{" "}
              {end ? `${sameDate ? "" : `${end.format("DD MMM")} `}${end.format("HH:mm")}` : "-"}
            </small>
          </td>
          <td>{period.stay}</td>
          <td className={`${styles.r} ${styles.tnum}`}>{fh(period.occupied_hours)}</td>
          <td>
            <span className={`${styles.rtag} ${styles[FRACTION_CLASS[fraction] || "free"]}`}>
              {RULE_LABELS[period.rule] || period.rule}
            </span>
          </td>
          <td className={styles.r}>
            <span className={`${styles.charged} ${fraction ? "" : styles.isFree}`}>
              {FRACTION_SHORT[fraction] ?? fraction}
            </span>
          </td>
          <td className={`${styles.r} ${styles.tnum}`}>
            {fraction ? formatAmount(period.amount) : "—"}
          </td>
        </tr>
      </React.Fragment>
    );
  });
};

const TestPanel = ({ facility, billingMode, configuration, currentPolicy }) => {
  const [stays, setStays] = useState(() => buildScenario("nights"));
  const [scenario, setScenario] = useState("nights");
  const [result, setResult] = useState(null);
  const [compare, setCompare] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  const labels = useMemo(() => stayLabels(stays), [stays]);
  const invalidConfig = configurationErrors(billingMode, configuration);
  const payloadStays = useMemo(() => toPayloadStays(stays, labels), [stays, labels]);
  const sameAsCurrent =
    !!currentPolicy &&
    currentPolicy.billing_mode === billingMode &&
    JSON.stringify(currentPolicy.configuration || {}) === JSON.stringify(configuration || {});
  const showCompare = !!currentPolicy && !sameAsCurrent;

  useEffect(() => {
    if (!facility?.id || !billingMode || invalidConfig) return undefined;
    if (!stays.length || stays.some((s) => !s.start || !s.end || !s.end.isAfter(s.start))) {
      setResult(null);
      return undefined;
    }

    const id = ++requestId.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const base = { facility_id: facility.id, stays: payloadStays };
        const [draft, inForce] = await Promise.all([
          previewBillingPolicyApi({ ...base, billing_mode: billingMode, configuration }),
          showCompare
            ? previewBillingPolicyApi({
                ...base,
                billing_mode: currentPolicy.billing_mode,
                configuration: currentPolicy.configuration || {},
              })
            : Promise.resolve(null),
        ]);
        if (id !== requestId.current) return;
        setResult(draft?.data?.data || null);
        setCompare(inForce?.data?.data || null);
        setError(null);
      } catch (e) {
        if (id !== requestId.current) return;
        setError(apiError(e, "Couldn't calculate the preview"));
        setResult(null);
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facility?.id, billingMode, JSON.stringify(configuration), payloadStays, showCompare]);

  const updateStay = (key, patch) => {
    setScenario(null);
    setStays((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
  };

  const applyScenario = (key) => {
    setScenario(key);
    setStays(buildScenario(key));
  };

  const addStay = () => {
    const last = stays[stays.length - 1];
    setScenario(null);
    setStays((prev) => [
      ...prev,
      stay("bed", last?.end || dayjs(), (last?.end || dayjs()).add(1, "day"), 1500),
    ]);
  };

  const baseTotal = sameAsCurrent ? result?.total_amount : compare?.total_amount;
  const difference =
    result && baseTotal != null ? Math.round((result.total_amount - baseTotal) * 100) / 100 : null;
  const deltaClass = difference === 0 ? styles.dSame : difference > 0 ? styles.dUp : styles.dDn;

  return (
    <aside className={`${styles.card} ${styles.test}`}>
      <div className={styles.testH}>
        <IconFlask size={17} />
        <h4>Test with sample stays</h4>
        <span className={styles.sp} />
        <span className={styles.hint}>Nothing is saved</span>
      </div>

      <div className={styles.scen}>
        {SCENARIOS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={scenario === s.key ? styles.on : ""}
            onClick={() => applyScenario(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className={styles.stays}>
        <div className={styles.stayHdr}>
          <span>Type</span>
          <span>Amount / day</span>
          <span>From</span>
          <span />
          <span>To</span>
          <span />
        </div>

        {stays.map((s) => (
          <div key={s.key} className={styles.stay}>
            <select
              className={styles.fi}
              value={s.kind}
              aria-label="Stay type"
              onChange={(event) => updateStay(s.key, { kind: event.target.value })}
            >
              <option value="bed">Bed</option>
              <option value="package">Package</option>
            </select>

            <div className={styles.rate}>
              <span>₹</span>
              <input
                value={s.rate}
                inputMode="decimal"
                title="Amount per day"
                aria-label="Amount per day"
                onChange={(event) =>
                  updateStay(s.key, { rate: event.target.value.replace(/[^\d.]/g, "") })
                }
              />
            </div>

            <input
              type="datetime-local"
              className={styles.fi}
              value={s.start ? s.start.format(FIELD) : ""}
              title="From"
              aria-label="From"
              onChange={(event) => updateStay(s.key, { start: dayjs(event.target.value) })}
            />
            <span className={styles.to}>
              <IconArrowRight size={13} />
            </span>
            <input
              type="datetime-local"
              className={styles.fi}
              value={s.end ? s.end.format(FIELD) : ""}
              title="To"
              aria-label="To"
              onChange={(event) => updateStay(s.key, { end: dayjs(event.target.value) })}
            />

            <button
              type="button"
              className={styles.del}
              title="Remove stay"
              style={stays.length < 2 ? { visibility: "hidden" } : undefined}
              disabled={stays.length < 2}
              onClick={() => {
                setScenario(null);
                setStays((prev) => prev.filter((x) => x.key !== s.key));
              }}
            >
              <IconTrash size={15} />
            </button>
          </div>
        ))}

        {stays.length < 10 && (
          <button type="button" className={styles.addStay} onClick={addStay}>
            <IconPlus size={15} width={2.4} />
            Add a stay
          </button>
        )}
      </div>

      <div className={styles.stayNote}>
        Beds are treated as one stay with transfers — each day goes to the bed used longest that
        day. Each package is priced on its own.
      </div>

      {invalidConfig ? (
        <div className={styles.empty}>Fix the highlighted steps to see the days.</div>
      ) : error ? (
        <Alert type="error" showIcon message={error} style={{ margin: "0 18px 14px" }} />
      ) : (
        <Spin spinning={loading}>
          <div className={styles.kpis}>
            <div className={styles.kpi}>
              <span>Billable days</span>
              <b>{result ? fh(result.billable_days) : "—"}</b>
            </div>
            <div className={styles.kpi}>
              <span>Total</span>
              <b>{result ? formatAmount(result.total_amount) : "—"}</b>
            </div>
            <div
              className={styles.kpi}
              title={currentPolicy ? MODE_LABELS[currentPolicy.billing_mode] : undefined}
            >
              <span>{currentPolicy ? `vs in force · v${currentPolicy.version}` : "vs in force"}</span>
              <b>{baseTotal != null ? formatAmount(baseTotal) : "—"}</b>
              {difference != null && (
                <small className={deltaClass}>
                  {sameAsCurrent
                    ? "this is the one in force"
                    : difference === 0
                    ? "same"
                    : `${difference > 0 ? "+" : "−"}${formatAmount(Math.abs(difference))}`}
                </small>
              )}
            </div>
          </div>

          {result?.periods?.length ? (
            <div className={styles.brk}>
              <table className={styles.tbl}>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Stay</th>
                    <th className={styles.r}>Hours</th>
                    <th>Rule</th>
                    <th className={styles.r}>Charged</th>
                    <th className={styles.r}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <PeriodRows periods={result.periods} />
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.empty}>Give each stay an end after its start to see the days.</div>
          )}
        </Spin>
      )}

      <div className={styles.tz}>
        <IconClock size={12} />
        Times are in the facility’s timezone, {facility?.timezone || "Asia/Kolkata"}.
      </div>
    </aside>
  );
};

export default TestPanel;
